/**
 * useAdminReservePayTransactions
 * Read-only hook for listing ReservePay (PromptPay auto) transactions in admin panel.
 */
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

export interface AdminReservePayTransaction {
  id: string
  reference_number: string
  transaction_type: 'credit_topup' | 'package_purchase'
  status: string
  amount: number
  credit_amount: number | null
  payment_method: string
  bank_code: string
  created_at: string
  updated_at: string
  external_reference: string | null
  metadata: {
    provider?: string
    flow?: string
    activation_choice?: string
    package_code?: string
    payment_method_scope?: string
  } | null
  customer_email: string | null
  business_name: string | null
  package_level: string | null
  package_display_name: string | null
  operational_status: string | null
  webhook_health: string | null
  last_provider_check_at: string | null
  last_provider_status_raw: string | null
  last_provider_status_mapped: string | null
  discrepancy_count: number
  needs_attention: boolean
}

export interface AdminReservePayTransactionsFilters {
  page?: number
  limit?: number
  status?: 'created' | 'pending' | 'completed' | 'failed' | 'expired' | 'cancelled'
  type?: 'credit_topup' | 'package_purchase'
  startDate?: string
  endDate?: string
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/payment`,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

export interface CrossCheckDiscrepancy {
  field: string
  local: string | number
  provider: string | number
  mappedProviderStatus?: string
  note: string
}

export interface CrossCheckResult {
  checkId: string
  local: {
    id: string
    referenceNumber: string
    externalReference: string | null
    status: string
    amount: number
    transactionType: string
    createdAt: string
    customerEmail: string | null
    businessName: string | null
  }
  provider: Record<string, unknown> | null
  providerError: string | null
  discrepancies: CrossCheckDiscrepancy[]
  webhookEvents: { event_name: string; processing_status: string; provider_payment_id: string | null; received_at: string }[]
  webhookLogs: { source: string; processing_status: string; error_message: string | null; received_at: string }[]
  webhookHealth: string
  operationalStatus: string
  recommendedActions: string[]
  crossCheckStatus: 'OK' | 'PROVIDER_UNREACHABLE' | 'DISCREPANCY_FOUND'
  checkedAt: string
}

export async function verifyReservePayTransaction(transactionId: string): Promise<CrossCheckResult> {
  const token = localStorage.getItem('authToken')
  const { data } = await axios.post(
    `${API_BASE_URL}/api/payment/admin/reservepay/transactions/${transactionId}/check-live`,
    {},
    { headers: { Authorization: token ? `Bearer ${token}` : '' } }
  )
  return data.data as CrossCheckResult
}

export interface ReservePayReconciliationPreview {
  transactionId: string
  providerPaymentId: string | null
  localStatus: string
  providerStatusRaw: string | null
  providerStatusMapped: string | null
  actionType: 'COMPLETE_PAYMENT' | 'CANCEL_PAYMENT' | 'FAIL_PAYMENT' | 'NOOP'
  allowed: boolean
  blockingReasons: string[]
  sideEffectsPreview: {
    willUpdateTransactionStatus: boolean
    willGrantCredits: boolean
    willActivatePackage: boolean
    willReverseCredits: boolean
    willCreateAuditLog: boolean
  }
  idempotencyKey: string
  checkId: string
}

export interface ReservePayReconciliationResult {
  reconciliationId: string | number
  transactionId: string
  actionType: string
  executionStatus: 'EXECUTED' | 'SKIPPED'
  transaction: {
    id: string
    status: string
    externalReference: string | null
  }
  sideEffects: {
    creditsGranted: number | null
    packageActivated: string | null
    auditLogWritten: boolean
  }
  providerSnapshot: {
    statusRaw: string | null
    statusMapped: string | null
  }
  executedAt?: string
}

export async function previewReservePayReconciliation(input: {
  transactionId: string
  checkId?: string
  reason: string
}): Promise<ReservePayReconciliationPreview> {
  const token = localStorage.getItem('authToken')
  const { data } = await axios.post(
    `${API_BASE_URL}/api/payment/admin/reservepay/transactions/${input.transactionId}/reconcile/preview`,
    {
      checkId: input.checkId,
      reason: input.reason,
    },
    { headers: { Authorization: token ? `Bearer ${token}` : '' } }
  )
  return data.data as ReservePayReconciliationPreview
}

export async function reconcileReservePayTransaction(input: {
  transactionId: string
  checkId?: string
  reason: string
  expectedLocalStatus?: string
}): Promise<ReservePayReconciliationResult> {
  const token = localStorage.getItem('authToken')
  const { data } = await axios.post(
    `${API_BASE_URL}/api/payment/admin/reservepay/transactions/${input.transactionId}/reconcile`,
    {
      checkId: input.checkId,
      reason: input.reason,
      expectedLocalStatus: input.expectedLocalStatus,
    },
    { headers: { Authorization: token ? `Bearer ${token}` : '' } }
  )
  return data.data as ReservePayReconciliationResult
}

export interface RefundReservePayInput {
  transactionId: string
  amount?: number
  reason?: string
}

export interface RefundReservePayResult {
  refund: {
    providerPaymentId: string
    amount: number
    reversedCredits: number | null
    reversedPackage: string | null
  }
}

export async function refundReservePayTransaction(input: RefundReservePayInput): Promise<RefundReservePayResult> {
  const token = localStorage.getItem('authToken')
  const { data } = await axios.post(
    `${API_BASE_URL}/api/payment/admin/reservepay/transactions/${input.transactionId}/refund`,
    {
      amount: input.amount,
      reason: input.reason,
    },
    { headers: { Authorization: token ? `Bearer ${token}` : '' } }
  )
  return data.data as RefundReservePayResult
}

export function useAdminReservePayTransactions(filters: AdminReservePayTransactionsFilters = {}) {
  return useQuery({
    queryKey: ['admin', 'reservepay-transactions', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters.page) params.page = filters.page
      if (filters.limit) params.limit = filters.limit
      if (filters.status) params.status = filters.status
      if (filters.type) params.type = filters.type
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate

      const { data } = await api.get('/admin/reservepay/transactions', { params })
      return data.data as {
        transactions: AdminReservePayTransaction[]
        pagination: { page: number; limit: number; total: number; pages: number; hasMore: boolean }
      }
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}
